import type { Theme, ThemeGroup } from "@albertine/types/openapi/lmt";
import type { ComponentType } from "react";
import { sortByStringComparator } from "../../common/utils/sort.util";
import { singularPlural } from "../../common/utils/time.formatted.util";
import { Flex } from "../Common/Flex";
import Label from "../Common/Label";
import Fold from "../Common/list/Fold";
import Tag from "../Common/Tag";
import LoadingSkeleton from "../LoadingSpinner/LoadingSkeleton";
import "./ThemeList.css";
import ThemeView from "./ThemeView";
import type { ThemeArticlesSource } from "./WithThemeAndArticles";
import WithThemeAndArticles from "./WithThemeAndArticles";

export type ThemeGroupPartition = "ungrouped" | string;
export type GroupedThemes = {
    group: ThemeGroup | null;
    themes: Theme[];
};

export type ThemeCardProps = {
    theme: Theme;
    showTargeting: boolean;
    groupedTheme: boolean;
    onThemeEditClick: (() => void) | undefined;
    onArticleClick: ((articleId: string) => void) | undefined;
};

type ThemeListProps = {
    spotlightThemes: Theme[];
    cityGuides: Theme[];
    articlesSource: ThemeArticlesSource;
    themeGroups: Record<ThemeGroupPartition, GroupedThemes>;
    ungrouped: GroupedThemes;
    ungroupedThemesHeading: string;
    onThemeEditClick: ((themeId: string) => void) | undefined;
    onArticleClick: ((themeId: string, articleId: string) => void) | undefined;
};

function ThemeList(props: ThemeListProps) {
    const {
        spotlightThemes,
        cityGuides,
        articlesSource,
        themeGroups,
        ungrouped,
        ungroupedThemesHeading,
        onThemeEditClick,
        onArticleClick,
    } = props;

    const onThemeEdit = (themeId: string) =>
        onThemeEditClick
            ? () => {
                  onThemeEditClick(themeId);
              }
            : undefined;

    const onArticleEdit = (themeId: string) =>
        onArticleClick
            ? (articleId: string) => {
                  onArticleClick(themeId, articleId);
              }
            : undefined;

    const zeroThemes = <Label.Medium.Light.N60>0 themes</Label.Medium.Light.N60>;

    return (
        <Flex.Column gap="large" className="theme-list">
            <h5>Spotlight</h5>

            {spotlightThemes.map((spotlightTheme) => (
                <WithThemeAndArticles
                    key={spotlightTheme.id}
                    themeId={spotlightTheme.id}
                    articlesSource={articlesSource}
                    skipThemeLoadWith={spotlightTheme}
                    renderOnError={<div />}
                    renderLoading={<LoadingSkeleton />}
                >
                    {(theme, articles) => (
                        <ThemeView
                            theme={theme}
                            articles={articles}
                            showTargeting
                            groupedTheme={false}
                            onThemeEditClick={onThemeEdit(theme.id)}
                            onArticleClick={onArticleEdit(theme.id)}
                        />
                    )}
                </WithThemeAndArticles>
            ))}

            {spotlightThemes.length === 0 && zeroThemes}

            <div />

            <h5>Dynamic themes</h5>

            {cityGuides.length === 0 &&
                Object.entries(themeGroups).length === 0 &&
                zeroThemes}

            {cityGuides.length !== 0 && (
                <Fold
                    title="City guides"
                    titleChildren={
                        <Tag.Medium.T90>
                            {singularPlural(
                                `${cityGuides.length} variation`,
                                `${cityGuides.length} variations`,
                                cityGuides.length,
                            )}
                        </Tag.Medium.T90>
                    }
                >
                    {cityGuides.map((guide) => (
                        <Flex.Row gap="medium" key={guide.id}>
                            <WithThemeAndArticles
                                key={guide.id}
                                themeId={guide.id}
                                articlesSource={articlesSource}
                                skipThemeLoadWith={guide}
                                renderOnError={<div />}
                                renderLoading={<LoadingSkeleton />}
                            >
                                {(theme, articles) => (
                                    <ThemeView
                                        theme={theme}
                                        articles={articles}
                                        showTargeting={false}
                                        groupedTheme
                                        onThemeEditClick={undefined}
                                        onArticleClick={onArticleEdit(theme.id)}
                                    />
                                )}
                            </WithThemeAndArticles>
                        </Flex.Row>
                    ))}
                </Fold>
            )}

            {Object.entries(themeGroups).length !== 0 &&
                Object.entries(themeGroups)
                    .sort(sortByStringComparator((it) => (it ? it[0] : undefined)))
                    .map(([key, themesGroup]) => (
                        <Fold
                            key={key}
                            title={themesGroup.group?.name || ""}
                            titleChildren={
                                <Tag.Medium.T90>
                                    {singularPlural(
                                        `${themesGroup.themes.length} variation`,
                                        `${themesGroup.themes.length} variations`,
                                        themesGroup.themes.length,
                                    )}
                                </Tag.Medium.T90>
                            }
                        >
                            {themesGroup.themes.map((groupTheme) => (
                                <Flex.Row gap="medium" key={groupTheme.id}>
                                    <WithThemeAndArticles
                                        key={groupTheme.id}
                                        themeId={groupTheme.id}
                                        articlesSource={articlesSource}
                                        skipThemeLoadWith={groupTheme}
                                        renderOnError={<div />}
                                        renderLoading={<LoadingSkeleton />}
                                    >
                                        {(theme, articles) => (
                                            <ThemeView
                                                theme={theme}
                                                articles={articles}
                                                showTargeting
                                                groupedTheme
                                                onThemeEditClick={onThemeEdit(theme.id)}
                                                onArticleClick={onArticleEdit(theme.id)}
                                            />
                                        )}
                                    </WithThemeAndArticles>
                                </Flex.Row>
                            ))}
                        </Fold>
                    ))}

            <div />
            <h5>{ungroupedThemesHeading}</h5>

            {ungrouped.themes.length === 0
                ? zeroThemes
                : ungrouped.themes.map((ungroupedTheme) => (
                      <Flex.Row gap="medium" key={ungroupedTheme.id}>
                          <WithThemeAndArticles
                              key={ungroupedTheme.id}
                              themeId={ungroupedTheme.id}
                              articlesSource={articlesSource}
                              skipThemeLoadWith={ungroupedTheme}
                              renderOnError={<div />}
                              renderLoading={<LoadingSkeleton />}
                          >
                              {(theme, articles) => (
                                  <ThemeView
                                      theme={theme}
                                      articles={articles}
                                      showTargeting
                                      groupedTheme={false}
                                      onThemeEditClick={onThemeEdit(theme.id)}
                                      onArticleClick={(article) => {
                                          onArticleClick
                                              ? onArticleClick(theme.id, article)
                                              : undefined;
                                      }}
                                  />
                              )}
                          </WithThemeAndArticles>
                      </Flex.Row>
                  ))}
        </Flex.Column>
    );
}

type ThemeListLoadingProps = {
    ungroupedThemesHeading: string;
};

export function ThemeListLoading(props: ThemeListLoadingProps) {
    const { ungroupedThemesHeading } = props;
    return (
        <Flex.Column gap="large" className="theme-list">
            <h5>Spotlight</h5>

            <LoadingSkeleton />

            <h5>Dynamic themes</h5>

            <LoadingSkeleton />

            <h5>{ungroupedThemesHeading}</h5>

            <LoadingSkeleton />
        </Flex.Column>
    );
}

export default ThemeList;
